/**
 * Hyperliquid public WebSocket subscriber for live position snapshots.
 *
 * Connects directly from the webapp to ``wss://api.hyperliquid.xyz/ws``
 * and subscribes to the ``webData2`` channel for the user's wallet
 * address — HL pushes the full ``clearinghouseState`` (positions, mark
 * prices, uPnL) on every tick. Used by ActiveTrade to render live
 * mark price + uPnL for HL perps without waiting for the 15-second
 * REST poll on ``/v1/balance/hl``.
 *
 * Singleton: a single WS connection per wallet address is shared
 * across all React consumers. The connection auto-opens on first
 * subscriber and auto-closes when the last unsubscribes.
 *
 * Mock mode (``?mock=1``): the WS open is skipped; instead a fake
 * driver emits drifting BTC/ETH/SOL prices every 250ms so the
 * dashboard's HL ActiveTrade card animates the same way the
 * PM card does under the mock-realtime-driver.
 */

import type { HlPositionItem } from './types';
import { isMockEnabled } from './mock';

const HL_WS_URL = 'wss://api.hyperliquid.xyz/ws';
/** Reconnect after a closed/errored socket — exponential up to a cap. */
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_CAP_MS = 30_000;

export interface HlSnapshot {
  positions: HlPositionItem[];
  /** Aggregate withdrawable from the same payload — surfaced for
   *  callers that want a unified view (HeroBalance), but the polling
   *  ``useHlBalance`` already covers that case so most consumers
   *  ignore this field. */
  withdrawable: string;
  account_value: string;
}

type Listener = (snap: HlSnapshot) => void;

interface Connection {
  address: string;
  ws: WebSocket | null;
  listeners: Set<Listener>;
  lastSnapshot: HlSnapshot | null;
  reconnectAttempt: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  mockTimer: ReturnType<typeof setInterval> | null;
  closed: boolean;
}

// One connection per user address — multi-account on the same device
// is rare but the shape is tolerant. Most sessions only ever see one.
const connections = new Map<string, Connection>();

/**
 * Subscribe to live HL position snapshots for ``address``. Returns
 * an unsubscribe function. If a snapshot has already arrived the
 * listener is invoked synchronously with it so first-render UI fills
 * immediately rather than waiting for the next tick.
 */
export function subscribeHlRealtime(
  address: string,
  listener: Listener,
): () => void {
  if (!address) {
    // Defensive — bail without throwing so callers can pass
    // ``me.data?.wallet_address ?? ''`` and let the no-op subscription
    // sort itself out once the wallet resolves.
    return () => {};
  }
  let conn = connections.get(address);
  if (!conn) {
    conn = {
      address,
      ws: null,
      listeners: new Set(),
      lastSnapshot: null,
      reconnectAttempt: 0,
      reconnectTimer: null,
      mockTimer: null,
      closed: false,
    };
    connections.set(address, conn);
    _open(conn);
  }
  conn.listeners.add(listener);
  if (conn.lastSnapshot) {
    listener(conn.lastSnapshot);
  }
  return () => {
    conn!.listeners.delete(listener);
    if (conn!.listeners.size === 0) {
      _teardown(conn!);
      connections.delete(address);
    }
  };
}

function _open(conn: Connection): void {
  if (isMockEnabled()) {
    _startMockDriver(conn);
    return;
  }
  if (typeof WebSocket === 'undefined') {
    return; // SSR / test env without ws polyfill — silently no-op
  }
  try {
    conn.ws = new WebSocket(HL_WS_URL);
  } catch {
    // ``new WebSocket`` throws synchronously on bad URL or CSP block.
    // Schedule a reconnect — a CSP error won't recover but the cap
    // bounds the retry storm; a transient error usually does.
    _scheduleReconnect(conn);
    return;
  }
  conn.ws.onopen = () => {
    conn.reconnectAttempt = 0;
    // ``webData2`` pushes the full ``clearinghouseState`` (positions
    // + bankroll) on every state change for the address. Same shape
    // as the REST ``user_state`` response, which the backend's
    // ``_parse_hl_positions`` parser already consumes — we just
    // re-implement that parse client-side.
    conn.ws?.send(
      JSON.stringify({
        method: 'subscribe',
        subscription: { type: 'webData2', user: conn.address.toLowerCase() },
      }),
    );
  };
  conn.ws.onmessage = (event) => {
    try {
      const parsed = JSON.parse(event.data);
      if (parsed?.channel !== 'webData2' || !parsed?.data) return;
      const snap = _parseSnapshot(parsed.data);
      if (snap === null) return;
      _publish(conn, snap);
    } catch {
      // Malformed frame from upstream — drop silently rather than
      // tear down the connection. The next valid frame will publish.
    }
  };
  conn.ws.onclose = () => {
    if (!conn.closed) _scheduleReconnect(conn);
  };
  conn.ws.onerror = () => {
    // ``onclose`` fires immediately after ``onerror`` so we don't
    // double-schedule. Letting the close handler do the reconnect.
  };
}

function _scheduleReconnect(conn: Connection): void {
  if (conn.closed) return;
  if (conn.reconnectTimer) clearTimeout(conn.reconnectTimer);
  const delay = Math.min(
    RECONNECT_CAP_MS,
    RECONNECT_BASE_MS * 2 ** conn.reconnectAttempt,
  );
  conn.reconnectAttempt += 1;
  conn.reconnectTimer = setTimeout(() => {
    conn.reconnectTimer = null;
    if (!conn.closed) _open(conn);
  }, delay);
}

function _teardown(conn: Connection): void {
  conn.closed = true;
  if (conn.reconnectTimer) {
    clearTimeout(conn.reconnectTimer);
    conn.reconnectTimer = null;
  }
  if (conn.mockTimer) {
    clearInterval(conn.mockTimer);
    conn.mockTimer = null;
  }
  if (conn.ws) {
    conn.ws.onopen = null;
    conn.ws.onmessage = null;
    conn.ws.onclose = null;
    conn.ws.onerror = null;
    if (
      conn.ws.readyState === WebSocket.OPEN ||
      conn.ws.readyState === WebSocket.CONNECTING
    ) {
      conn.ws.close();
    }
    conn.ws = null;
  }
}

function _publish(conn: Connection, snap: HlSnapshot): void {
  conn.lastSnapshot = snap;
  for (const l of conn.listeners) l(snap);
}

/**
 * Parse the same raw ``clearinghouseState`` shape that
 * ``apps/api/.../balance_hl.py::_parse_hl_positions`` handles — only
 * extracts the per-perp detail ActiveTrade needs. Defensive against
 * missing keys, ``null`` values where dicts are expected, NaN /
 * Infinity numerics. Returns ``null`` only if the entire shape is
 * unrecognisable; otherwise returns a snapshot with ``positions=[]``.
 */
export function _parseSnapshot(raw: unknown): HlSnapshot | null {
  if (!_isRecord(raw)) return null;
  const ch = _isRecord(raw['clearinghouseState'])
    ? (raw['clearinghouseState'] as Record<string, unknown>)
    : raw;
  if (!_isRecord(ch)) return null;
  const summary = _isRecord(ch['crossMarginSummary'])
    ? (ch['crossMarginSummary'] as Record<string, unknown>)
    : {};
  const accountValue = _str(summary['accountValue']) ?? '0';
  const withdrawable = _str(ch['withdrawable']) ?? '0';
  const positions: HlPositionItem[] = [];
  const apList = ch['assetPositions'];
  if (Array.isArray(apList)) {
    for (const ap of apList) {
      const item = _parsePosition(ap);
      if (item) positions.push(item);
    }
  }
  return { positions, withdrawable, account_value: accountValue };
}

function _parsePosition(ap: unknown): HlPositionItem | null {
  if (!_isRecord(ap)) return null;
  const pos = _isRecord(ap['position']) ? (ap['position'] as Record<string, unknown>) : null;
  if (!pos) return null;
  const coin = pos['coin'];
  if (typeof coin !== 'string' || !coin) return null;
  const sziStr = _str(pos['szi']);
  const szi = sziStr !== null ? Number(sziStr) : NaN;
  if (!Number.isFinite(szi) || szi === 0) return null;
  const entryStr = _str(pos['entryPx']);
  const positionValueStr = _str(pos['positionValue']);
  const upnlStr = _str(pos['unrealizedPnl']);
  if (entryStr === null || positionValueStr === null || upnlStr === null) {
    return null;
  }
  const positionValue = Number(positionValueStr);
  if (!Number.isFinite(positionValue)) return null;
  const sizeAbs = Math.abs(szi);
  const markPrice = sizeAbs > 0 ? positionValue / sizeAbs : 0;
  return {
    coin,
    size: sziStr!,
    side: szi > 0 ? 'LONG' : 'SHORT',
    entry_price: entryStr,
    mark_price: markPrice.toString(),
    position_value: positionValueStr,
    unrealized_pnl: upnlStr,
  };
}

function _isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function _str(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

// ─── Mock driver ───────────────────────────────────────────────
//
// Mirrors what ``startMockRealtimeDriver`` does for ``global:price``:
// emit a synthetic snapshot every 250ms with drifting mark prices so
// the HL ActiveTrade card animates under ``?mock=1``. The driver
// reads from an in-memory base set rather than querying the fixtures
// — this keeps it self-contained even when consumers stub out
// ``/v1/balance/hl``.

interface MockBase {
  coin: string;
  size: number; // signed
  entry: number;
  initialMark: number;
}

const MOCK_BASE: MockBase[] = [
  { coin: 'BTC', size: 0.05, entry: 60_000, initialMark: 60_500 },
  { coin: 'ETH', size: -2.0, entry: 3_000, initialMark: 2_950 },
];

function _startMockDriver(conn: Connection): void {
  if (conn.mockTimer) return;
  // Per-conn drift so multiple address-tabs each get their own walk.
  const marks = MOCK_BASE.map((b) => b.initialMark);
  const tick = () => {
    if (conn.closed) return;
    for (let i = 0; i < marks.length; i++) {
      // ±0.05% per 250ms tick — visible movement without wild swings.
      const drift = (Math.random() - 0.5) * 0.001;
      marks[i] = Math.max(0.01, marks[i] * (1 + drift));
    }
    const positions: HlPositionItem[] = MOCK_BASE.map((b, i) => {
      const mark = marks[i];
      const sizeAbs = Math.abs(b.size);
      const positionValue = mark * sizeAbs;
      // uPnL formula: (mark − entry) × size, i.e. signed by direction.
      const upnl = (mark - b.entry) * b.size;
      return {
        coin: b.coin,
        size: b.size.toString(),
        side: b.size > 0 ? 'LONG' : 'SHORT',
        entry_price: b.entry.toString(),
        mark_price: mark.toFixed(2),
        position_value: positionValue.toFixed(2),
        unrealized_pnl: upnl.toFixed(2),
      };
    });
    _publish(conn, {
      positions,
      withdrawable: '312.40',
      account_value: '598.10',
    });
  };
  // Fire once synchronously so the first render has data.
  tick();
  conn.mockTimer = setInterval(tick, 250);
}

/** Test helper — drop every active connection so unit tests don't
 *  leak listeners or timers across cases. */
export function __resetHlRealtime(): void {
  for (const conn of connections.values()) {
    _teardown(conn);
  }
  connections.clear();
}
